import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  Optional,
  Input,
  Output,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogModule } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ActivityAssociationInterface,
  ActivityInterface,
  CrmObjectInterface,
  FieldValueInterface,
  StringListInterface,
} from '@vendasta/crm';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyUploaderModule } from '@vendasta/galaxy/uploader';
import { FileInfo, UploaderAccessTypeOption } from '@vendasta/galaxy/uploader';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { User } from '@vendasta/iamv2';
import { FileAccessType } from '@vendasta/media';
import { switchWhenFalsy } from '@vendasta/rx-utils';
import { ObservableWorkState } from '@vendasta/rx-utils/work-state';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  firstValueFrom,
  from,
  map,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { distinctUntilChanged, filter, mergeMap, startWith, withLatestFrom } from 'rxjs/operators';
import { ChipListComponent, ChipListItem } from '../../chip-list';
import { DropDownOptionsButton } from '../../chip-list/interfaces';
import { CrmUnknownUserAvatarComponent } from '../../unknown-user-avatar/unknown-user-avatar.component';
import {
  ActivityType,
  AssociationFormField,
  CommunicationSubType,
  CrmDependencies,
  CrmInjectionToken,
  CrmObjectDependencies,
  CrmObjectInjectionToken,
  ObjectType,
} from '../../../tokens-and-interfaces';
import { AVATAR_WIDTH, SNACKBAR_DURATION } from '../../../constants';

import { TranslationModule } from '../../../i18n';
import { ActivityLoggerService } from './activity-logger.service';
import {
  CrmActivityLoggerCallComponent,
  CrmActivityLoggerEmailComponent,
  CrmActivityLoggerFollowUpTaskComponent,
  CrmActivityLoggerMeetingComponent,
  CrmActivityLoggerNoteComponent,
  CrmActivityLoggerTagsComponent,
  CrmActivityLoggerTaskComponent,
  CrmLoggerContentDirective,
  LoggerComponentInterface,
} from './logger';
import { CrmActivityLoggerCommunicationComponent } from './logger/communication.component';
import { CrmFieldService, PlatformExtensionFieldIds, StandardIds } from '@galaxy/crm/services/field-service';
import {
  CrmAssociationService,
  CrmObjectDisplayService,
  CrmObjectService,
  FileAssociationService,
  FileAssociationManager,
  isFileUploadBatchSettled,
} from '../../../shared-services';
import {
  CrmMultiObjectAssociationFormFieldComponent,
  CrmObjectAssociationFormFieldComponent,
  CrmSalesOpportunityAssociationFormFieldComponent,
  CrmTeamAssociationFormFieldComponent,
  FormFieldAssociation,
} from '../../association-form-field';

const DEFAULT_SELECTED_ACTIVITY_TYPE: ActivityType = 'Note';
// matches the `sm` column size
const DEFAULT_MOBILE_BREAKPOINT = '600px';

type SelectableActivityType = ChipListItem<ActivityType>;

const SELECTABLE_ACTIVITY_TYPES: SelectableActivityType[] = [
  {
    value: 'Note',
    label: 'ACTIVITY_TYPES.NOTE',
    icon: 'notes',
  },
  {
    value: 'Email',
    label: 'ACTIVITY_TYPES.EMAIL',
    icon: 'email',
  },
  {
    value: 'Call',
    label: 'ACTIVITY_TYPES.CALL',
    icon: 'phone',
  },
  {
    value: 'Meeting',
    label: 'ACTIVITY_TYPES.MEETING',
    icon: 'calendar_today',
  },
  {
    value: 'Task',
    label: 'ACTIVITY_TYPES.TASK',
    icon: 'assignment_turned_in',
  },
];

type SelectableCommunicationType = DropDownOptionsButton<CommunicationSubType>;

const SELECTABLE_COMMUNICATION_TYPES: SelectableCommunicationType[] = [
  {
    value: 'SMS',
    label: 'ACTIVITY_TYPES.COMMUNICATION_SUBTYPES.SMS',
  },
  {
    value: 'Inbox',
    label: 'ACTIVITY_TYPES.COMMUNICATION_SUBTYPES.INBOX',
  },
  {
    value: 'LinkedIn',
    label: 'ACTIVITY_TYPES.COMMUNICATION_SUBTYPES.LINKEDIN',
  },
];

type Layout = 'auto' | 'mobile';

// Use the shared interface from the service
import { FileAssociationData } from '../../../shared-services/file-association.service';
type ActivityFileAssociationData = FileAssociationData;

const manuallyLoggedFieldValue: FieldValueInterface = {
  fieldId: StandardIds.ActivityManuallyLogged,
  booleanValue: true,
};

@Component({
  selector: 'crm-activity-logger',
  templateUrl: './activity-logger.component.html',
  styleUrls: ['./activity-logger.component.scss', './logger/shared.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    MatCardModule,
    MatIconModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatButtonModule,
    GalaxyAvatarModule,
    GalaxyFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    CrmUnknownUserAvatarComponent,
    MatTooltipModule,
    GalaxyButtonLoadingIndicatorModule,
    ChipListComponent,
    CrmLoggerContentDirective,
    CrmActivityLoggerNoteComponent,
    CrmActivityLoggerEmailComponent,
    CrmActivityLoggerCallComponent,
    CrmActivityLoggerMeetingComponent,
    CrmActivityLoggerTaskComponent,
    CrmTeamAssociationFormFieldComponent,
    CrmSalesOpportunityAssociationFormFieldComponent,
    CrmActivityLoggerCommunicationComponent,
    MatMenuModule,
    MatDialogModule,
    GalaxyUploaderModule,
    CrmActivityLoggerTagsComponent,
    CrmActivityLoggerFollowUpTaskComponent,
    CrmObjectAssociationFormFieldComponent,
    CrmMultiObjectAssociationFormFieldComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ActivityLoggerService, FileAssociationService],
})
export class CrmActivityLoggerComponent {
  @ViewChild(CrmActivityLoggerNoteComponent) loggerNoteComponent?: CrmActivityLoggerNoteComponent;
  @ViewChild(CrmActivityLoggerEmailComponent) loggerEmailComponent?: CrmActivityLoggerEmailComponent;
  @ViewChild(CrmActivityLoggerCallComponent) loggerCallComponent?: CrmActivityLoggerCallComponent;
  @ViewChild(CrmActivityLoggerMeetingComponent) loggerMeetingComponent?: CrmActivityLoggerMeetingComponent;
  @ViewChild(CrmActivityLoggerTaskComponent) loggerTaskComponent?: CrmActivityLoggerTaskComponent;
  @ViewChild(CrmActivityLoggerCommunicationComponent)
  loggerCommunicationComponent?: CrmActivityLoggerCommunicationComponent;
  @ViewChild(CrmActivityLoggerFollowUpTaskComponent)
  loggerFollowUpTaskComponent?: CrmActivityLoggerFollowUpTaskComponent;

  AVATAR_WIDTH = AVATAR_WIDTH;
  SELECTABLE_ACTIVITY_TYPES = SELECTABLE_ACTIVITY_TYPES;
  SELECTABLE_COMMUNICATION_TYPES = SELECTABLE_COMMUNICATION_TYPES;
  activityTypeSelected?: ActivityType;
  communicationSubTypeSelected?: CommunicationSubType;
  collapsed = signal<boolean>(true);
  sharedDescription: string | null = '';

  private readonly layout$$: BehaviorSubject<Layout> = new BehaviorSubject<Layout>('auto');
  readonly mobileLayout$: Observable<boolean>;

  private readonly objectType$$ = new ReplaySubject<ObjectType>(1);
  protected objectType$ = this.objectType$$.asObservable();
  @Input({ required: true }) set objectType(value: ObjectType) {
    this.objectType$$.next(value);
  }

  private readonly crmObjectId$$ = new ReplaySubject<string>(1);
  protected crmObjectId$ = this.crmObjectId$$.asObservable();
  @Input({ required: true }) set crmObjectId(value: string) {
    this.crmObjectId$$.next(value);
  }

  public user$ = this.activityLoggerService.user$;
  protected activityOwnerId$: Observable<string>;
  @Input() activityOwner?: User;

  /**
   * Associations to be added to the activity. These are not visible to the user.
   */
  @Input() extraAssociations?: ActivityAssociationInterface[];
  /**
   * Hide Company and Contact association fields.
   */
  @Input() hideCrmObjectAssociations = false;

  /**
   * Expand and select the activity type when provided.
   */
  @Input() set expandAndSelectType(value: string | undefined) {
    const activityType = value ? (value as ActivityType) : DEFAULT_SELECTED_ACTIVITY_TYPE;
    this.onActivityTypeChanged(activityType);
  }

  /**
   * Layout of the activity logger.
   * Use `mobile` to display the activity logger in a mobile layout disregarding the screen size.
   */
  @Input() set layout(value: Layout) {
    this.layout$$.next(value);
  }
  /**
   * When an Activity ID is passed in assume we are editing.
   */
  private readonly crmActivityId$$ = new BehaviorSubject<string>('');
  protected crmActivityId$ = this.crmActivityId$$.asObservable();
  protected isEditing$: Observable<boolean>;
  @Input() set crmActivityId(value: string) {
    this.crmActivityId$$.next(value);
  }

  @Output() activityCreated: EventEmitter<ActivityInterface> = new EventEmitter();
  @Output() activityUpdated: EventEmitter<ActivityInterface> = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() cancel: EventEmitter<void> = new EventEmitter();

  activity$$ = new BehaviorSubject<ActivityInterface | null>(null);
  activity$ = this.activity$$.asObservable();

  protected teamAssociations = signal<FormFieldAssociation[]>([]);
  protected objectAssociations = signal<AssociationFormField[]>([]);
  protected availableObjectTypes$: Observable<ObjectType[]>;
  protected hasOpportunityType$: Observable<boolean>;

  protected get contactAssociations(): FormFieldAssociation[] {
    return this.objectAssociations().find((field) => field.type === 'Contact')?.associations || [];
  }
  protected set contactAssociations(newAssociations: FormFieldAssociation[]) {
    this.objectAssociations.update((fields) => {
      return fields.map((field) => (field.type === 'Contact' ? { ...field, associations: newAssociations } : field));
    });
  }

  protected get companyAssociations(): FormFieldAssociation[] {
    return this.objectAssociations().find((field) => field.type === 'Company')?.associations || [];
  }
  protected set companyAssociations(newAssociations: FormFieldAssociation[]) {
    this.objectAssociations.update((fields) => {
      return fields.map((field) => (field.type === 'Company' ? { ...field, associations: newAssociations } : field));
    });
  }

  protected get opportunityAssociations(): FormFieldAssociation[] {
    return this.objectAssociations().find((field) => field.type === 'Opportunity')?.associations || [];
  }
  protected set opportunityAssociations(newAssociations: FormFieldAssociation[]) {
    this.objectAssociations.update((fields) => {
      return fields.map((field) =>
        field.type === 'Opportunity' ? { ...field, associations: newAssociations } : field,
      );
    });
  }

  tags$$ = new BehaviorSubject<string[]>([]);
  tags$ = this.tags$$.asObservable();

  private readonly upsertActivityWorkState$ = new ObservableWorkState<string[]>();
  protected readonly upsertActivityButtonLoading$ = this.upsertActivityWorkState$.isLoading$;

  protected showTeamMemberInput = false;
  protected showOpportunityAssociation$!: Observable<boolean>;
  protected showAssociationModal$ = this.config.hasCrmAssociationModalFeatureFlag$ ?? of(false);
  protected readonly primaryAccountGroupId$: Observable<string | null>;
  private readonly crmObjectName$: Observable<string>;
  private readonly crmObject$: Observable<CrmObjectInterface>;
  private readonly followUpExpanded$$ = new BehaviorSubject<boolean>(false);
  protected enableNewActivityFeed = toSignal(this.config?.enableNewActivityFeed$ ?? of(false));
  protected showFileAssociations = toSignal(this.config?.activity?.showFileAssociations$ ?? of(false), {
    initialValue: false,
  });

  // File Association Properties
  showFileUploader = false;

  // File association manager from service
  private fileAssociationManager: FileAssociationManager = this.fileAssociationService.createAssociationManager(
    'Activity' as any,
    '', // Will be set dynamically when activity ID is available
    {
      immediateAssociation: false, // Don't create associations immediately, wait for activity creation
      immediateRemoval: false, // Don't delete associations immediately, handle via activity save
    },
  );

  // Expose the associated files signal from the service
  associatedFiles = toSignal(this.fileAssociationManager.associatedFiles$, {
    initialValue: [] as ActivityFileAssociationData[],
  });

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly displayService: CrmObjectDisplayService,
    private activityLoggerService: ActivityLoggerService,
    private crmObjectService: CrmObjectService,
    private readonly snackService: SnackbarService,
    private readonly fieldsService: CrmFieldService,
    private readonly associationService: CrmAssociationService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly translateService: TranslateService,
    private readonly fileAssociationService: FileAssociationService,
    @Optional() @Inject(CrmObjectInjectionToken) private readonly crmObjectDependencies: CrmObjectDependencies,
  ) {
    this.crmActivityId$
      .pipe(
        takeUntilDestroyed(),
        switchMap((crmActivityId) =>
          crmActivityId ? this.activityLoggerService.getActivity(crmActivityId) : of(null),
        ),
        tap((activity) => {
          this.activity$$.next(activity);
          if (activity) {
            const tags = this.fieldsService.getFieldValueFromCrmObject(activity, StandardIds.ActivityTags);
            this.tags$$.next(tags?.stringValues?.values || []);
          } else {
            this.tags$$.next([]);
          }
        }),
      )
      .subscribe();
    this.activityOwnerId$ = combineLatest([this.activity$, this.user$]).pipe(
      takeUntilDestroyed(),
      map(([activity, user]): string => activity?.ownerId || user?.userId || ''),
      distinctUntilChanged(),
    );

    this.isEditing$ = this.crmActivityId$.pipe(map((crmActivityId) => Boolean(crmActivityId)));
    this.showTeamMemberInput = Boolean(this.config?.services?.userService?.searchUsers);
    if (this.crmObjectDependencies) {
      this.showOpportunityAssociation$ = this.crmObjectDependencies.showOpportunityAssociation$ ?? of(false);
    } else {
      this.showOpportunityAssociation$ = this.objectType$.pipe(
        switchMap((objectType) => {
          switch (objectType) {
            case 'Contact':
              return this.config.contact?.showOpportunityAssociation$ ?? of(false);
            case 'Company':
              return this.config.company?.showOpportunityAssociation$ ?? of(false);
            case 'Opportunity':
              return this.config.opportunity?.showOpportunityAssociation$ ?? of(false);
            case 'CustomObject':
              return this.config.customobject?.showOpportunityAssociation$ ?? of(false);
            default:
              return of(false);
          }
        }),
        startWith(false),
      );
    }
    this.crmObject$ = combineLatest([this.crmObjectId$, this.objectType$]).pipe(
      switchMap(([crmObjectId, crmObjectType]) => this.crmObjectService.getMultiObject(crmObjectType, [crmObjectId])),
      map((response) => response?.crmObjects?.[0]),
      filter(Boolean),
      shareReplay(1),
    );

    this.crmObjectName$ = combineLatest([this.crmObject$, this.objectType$]).pipe(
      map(([crmObject, objectType]) => this.displayService.getObjectDisplayName(objectType, crmObject)),
    );
    this.primaryAccountGroupId$ = this.crmObject$.pipe(map((crmObject) => this.getPrimaryAccountGroupId(crmObject)));
    this.availableObjectTypes$ = combineLatest([
      this.primaryAccountGroupId$,
      this.config.parentNamespace$,
      this.showOpportunityAssociation$,
    ]).pipe(
      map(([accountGroupId, parentNamespace, showOpportunity]) => {
        const base: ObjectType[] = ['Contact', 'Company', 'CustomObject'];
        const canShowOpp = (parentNamespace || accountGroupId) && showOpportunity && !this.hideCrmObjectAssociations;
        return canShowOpp ? ([...base, 'Opportunity'] as ObjectType[]) : base;
      }),
      startWith([] as ObjectType[]),
    );
    this.hasOpportunityType$ = this.availableObjectTypes$.pipe(map((types) => types.includes('Opportunity')));

    this.mobileLayout$ = this.setupMobileMatcher();

    this.upsertActivityWorkState$.workResults$
      .pipe(
        takeUntilDestroyed(),
        switchMap((activityIds) => from(activityIds)),
        mergeMap((activityId) =>
          // Create file associations with parent object after activity creation, then fetch the activity
          combineLatest([of(activityId), this.isEditing$, this.objectType$, this.crmObjectId$]).pipe(
            take(1),
            switchMap(([currentActivityId, _isEditing, objectType, objectId]) => {
              return this.handlePostSaveFileAssociations(currentActivityId, objectType, objectId);
            }),
          ),
        ),
        tap((activity) => {
          const wasCreated = activity?.created?.getTime() === activity?.updated?.getTime();
          if (wasCreated) {
            this.activityCreated.emit(activity);
          } else {
            this.activityUpdated.emit(activity);
          }
          // Clear uploaded files after successful activity creation and parent object association
          this.clearUploadedFiles();
        }),
      )
      .subscribe();

    this.upsertActivityWorkState$.successEvents$.pipe(takeUntilDestroyed()).subscribe(async (success) => {
      const isEditing = await firstValueFrom(this.isEditing$);
      const successMsg = isEditing ? 'ACTIVITY_LOGGER.EDIT_SUCCESS_MESSAGE' : 'ACTIVITY_LOGGER.SUCCESS_MESSAGE';
      const errorMsg = isEditing ? 'ACTIVITY_LOGGER.EDIT_ERROR_MESSAGE' : 'ACTIVITY_LOGGER.ERROR_MESSAGE';
      if (!success) {
        this.snackService.openErrorSnack(errorMsg, {
          duration: SNACKBAR_DURATION,
        });
      } else {
        this.snackService.openSuccessSnack(successMsg, {
          duration: SNACKBAR_DURATION,
        });
        if (!isEditing) this.collapse();
      }
    });

    this.initializeActivity();
  }

  private initializeActivity() {
    toObservable(this.collapsed)
      .pipe(
        takeUntilDestroyed(),
        filter((isCollapsed) => !isCollapsed),
        withLatestFrom(this.isEditing$),
        switchMap(([_, isEditing]) => {
          if (isEditing) {
            return this.crmActivityId$.pipe(
              switchMap((crmActivityId) => this.activityLoggerService.getActivity(crmActivityId)),
              map((activity) => ({
                activity: activity,
                isEditing: isEditing,
              })),
            );
          } else {
            return of({
              activity: {} as ActivityInterface,
              isEditing: isEditing,
            });
          }
        }),
      )
      .subscribe((activity) => {
        // Set the default values for the logger
        const activityType = activity.activity?.crmObjectSubtype as ActivityType;
        if (activityType) {
          this.onActivityTypeChanged(activityType);
        }
        if (activity.activity) {
          this.updateValueAndValidity();
        }

        if (!activity.isEditing) {
          this.setDefaultActivity();
        } else {
          this.setDefaultObjectAssociations();
          this.setAssociationValues(activity.activity.associations ?? [], activity.activity);
        }
      });
  }

  private setDefaultActivity(): void {
    this.setDefaultUserAssociation();

    this.setDefaultObjectAssociations();
    this.setDefaultAssociationsByObjectType('Contact');
    this.setDefaultAssociationsByObjectType('Company');
    this.setDefaultAssociationsByObjectType('CustomObject');
    this.setDefaultCurrentObjectAssociation();
  }

  private setDefaultCurrentObjectAssociation(): void {
    combineLatest([this.objectType$, this.crmObject$])
      .pipe(take(1))
      .subscribe(([objectType, crmObject]) => {
        const objectDefault: FormFieldAssociation = {
          name: this.displayService.getObjectDisplayName(objectType, crmObject),
          associationInfo: this.displayService.getAssociationInfo(objectType, crmObject),
          crmObjectId: crmObject.crmObjectId,
          crmObjectType: objectType,
          crmObjectSubtype: crmObject.crmObjectSubtype,
        };

        this.setAssociationsInObjectAssociations(objectType, [objectDefault], false);
      });
  }

  private setDefaultUserAssociation(): void {
    this.user$.pipe(take(1)).subscribe((user) =>
      this.teamAssociations.update((existing) => {
        const userDefault: FormFieldAssociation = {
          crmObjectId: user?.userId || '',
          crmObjectType: 'User',
        };
        return [...existing, userDefault];
      }),
    );
  }

  private setDefaultAssociationsByObjectType(objectType: ObjectType): void {
    this.getDefaultAssociationsByObjectType(objectType)
      .pipe(take(1))
      .subscribe((associations) => this.setAssociationsInObjectAssociations(objectType, associations, false));
  }

  private setDefaultObjectAssociations(): void {
    this.objectAssociations.set([
      {
        type: 'Contact',
        associations: [],
      } as AssociationFormField,
      {
        type: 'Company',
        associations: [],
      } as AssociationFormField,
      {
        type: 'Opportunity',
        associations: [],
      } as AssociationFormField,
      {
        type: 'CustomObject',
        associations: [],
      } as AssociationFormField,
    ]);
  }

  getDefaultAssociationsByObjectType(
    associationObjectType: ObjectType,
    hideForObjectTypes?: ObjectType[],
  ): Observable<FormFieldAssociation[]> {
    return combineLatest([this.objectType$, this.crmObjectId$]).pipe(
      switchMap(([objectType, objectId]) => {
        if (this.hideCrmObjectAssociations || hideForObjectTypes?.find((type) => type === objectType)) {
          return of([]);
        }
        return this.associationService.getAssociationsWithAssociatedObjectForObject$(
          objectId,
          objectType,
          associationObjectType,
        );
      }),
      map((associations) => {
        return associations.map((association) => {
          return {
            name: this.displayService.getObjectDisplayName(associationObjectType, association.associatedObject),
            crmObjectType: associationObjectType,
            crmObjectSubtype: association.associatedObject.crmObjectSubtype,
            crmObjectId: association.associatedObject.crmObjectId,
          } as FormFieldAssociation;
        });
      }),
      catchError((_: any) => {
        return of([] as FormFieldAssociation[]);
      }),
    );
  }

  protected handleObjectAssociationsUpdate(updatedAssociations: AssociationFormField[]): void {
    this.objectAssociations.set([...updatedAssociations]);
  }

  onActivityTypeChanged(value: ActivityType | null) {
    this.expand();
    this.activityTypeSelected = value || undefined;
    this.communicationSubTypeSelected = undefined;
  }

  onCommunicationTypeChanged(activityType: ActivityType, communicationSubType: CommunicationSubType) {
    this.expand();
    this.activityTypeSelected = activityType;
    this.communicationSubTypeSelected = communicationSubType;
  }

  getTranslatedCommunicationType(): string {
    return this.communicationSubTypeSelected
      ? `ACTIVITY_TYPES.COMMUNICATION_SUBTYPES.${this.communicationSubTypeSelected.toUpperCase()}`
      : 'ACTIVITY_TYPES.MORE';
  }

  upsertActivity() {
    if (!this.validateForm()) {
      this.snackService.openErrorSnack('ACTIVITY_LOGGER.ERROR_SET_REQUIRED_FIELDS', {
        duration: SNACKBAR_DURATION,
      });
      return;
    }

    const fieldValues = this.getSelectedComponent()?.getValues() || [];
    const ownerId = this.getSelectedComponent()?.ownerId;
    const work$ = combineLatest([this.isEditing$, this.tags$, this.crmObjectName$, this.followUpExpanded$$]).pipe(
      switchMap(([isEditing, tags, crmObjectName, isFollowUpExpanded]) => {
        const tagsField: FieldValueInterface = {
          fieldId: StandardIds.ActivityTags,
          stringValues: <StringListInterface>{ values: tags },
        };
        let apiCalls: Observable<string>[];
        if (isEditing) {
          apiCalls = [
            this.crmActivityId$.pipe(
              switchMap((activityId) => {
                const fieldMask = [
                  'associations',
                  ...this.fieldsService.getFieldMaskFromFormGroup(this.getSelectedComponent()?.formGroup),
                  StandardIds.ActivityTags,
                ];
                if (ownerId && ownerId !== this.activity$$.getValue()?.ownerId) {
                  fieldMask.push('ownerId');
                }
                return this.activityLoggerService.updateActivity(
                  activityId,
                  this.activityTypeSelected!,
                  [...fieldValues, tagsField],
                  this.mergeAssociations(),
                  ownerId,
                  fieldMask,
                );
              }),
            ),
          ];
        } else {
          apiCalls = [
            this.activityLoggerService.addActivity(
              this.activityTypeSelected!,
              [...fieldValues, manuallyLoggedFieldValue, tagsField],
              this.mergeAssociations(),
              ownerId,
            ),
          ];
          if (isFollowUpExpanded) {
            const followUpFieldValues = this.loggerFollowUpTaskComponent?.getValues(crmObjectName) || [];
            apiCalls.push(
              this.activityLoggerService.addActivity(
                'Task',
                [...followUpFieldValues, manuallyLoggedFieldValue],
                this.mergeAssociations(),
                ownerId,
              ),
            );
          }
        }
        return combineLatest(apiCalls);
      }),
    );

    this.upsertActivityWorkState$.startWork(work$);
  }

  private getSelectedComponent(): LoggerComponentInterface | undefined {
    switch (this.activityTypeSelected) {
      case 'Note':
        return this.loggerNoteComponent;
      case 'Email':
        return this.loggerEmailComponent;
      case 'Call':
        return this.loggerCallComponent;
      case 'Meeting':
        return this.loggerMeetingComponent;
      case 'Task':
        return this.loggerTaskComponent;
      case 'Communication':
        return this.loggerCommunicationComponent;
      default:
        return;
    }
  }

  isFormValid(): boolean {
    return Boolean(this.getSelectedComponent()?.formGroup?.valid);
  }

  validateForm(): boolean {
    this.updateValueAndValidity();
    return this.isFormValid();
  }

  setAssociationValues(associations: ActivityAssociationInterface[], _activity?: ActivityInterface): void {
    const teamAssociations = associations
      .filter((item) => item.crmObjectType === 'User')
      .map((association) => {
        return {
          crmObjectId: association.crmObjectId,
          crmObjectType: association.crmObjectType,
        } as FormFieldAssociation;
      });
    this.teamAssociations.set(teamAssociations);

    const opportunityAssociations = associations
      .filter((item) => item.crmObjectType === 'Opportunity')
      .map((association) => {
        return {
          crmObjectId: association.crmObjectId,
          crmObjectType: association.crmObjectType,
        } as FormFieldAssociation;
      });
    this.setAssociationsInObjectAssociations('Opportunity', opportunityAssociations, true);

    const contactAssociations = associations
      .filter((item) => item.crmObjectType === 'Contact')
      .map((association) => {
        return {
          crmObjectId: association.crmObjectId,
          crmObjectType: association.crmObjectType,
        } as FormFieldAssociation;
      });
    this.setAssociationsByObjectType('Contact', contactAssociations);

    const companyAssociations = associations
      .filter((item) => item.crmObjectType === 'Company')
      .map((association) => {
        return {
          crmObjectId: association.crmObjectId,
          crmObjectType: association.crmObjectType,
        } as FormFieldAssociation;
      });
    this.setAssociationsByObjectType('Company', companyAssociations);

    const customObjectAssociation = associations
      .filter((item) => item.crmObjectType === 'CustomObject')
      .map((association) => {
        return {
          crmObjectId: association.crmObjectId,
          crmObjectType: association.crmObjectType,
          crmObjectSubtype: association.crmObjectSubtype,
        } as FormFieldAssociation;
      });
    this.setAssociationsByObjectType('CustomObject', customObjectAssociation);

    // Load existing file associations for editing mode
    this.fileAssociationService.loadExistingFileAssociationsFromActivity(associations).pipe(take(1)).subscribe();
  }

  private async setAssociationsByObjectType(
    objectType: ObjectType,
    associations: FormFieldAssociation[],
  ): Promise<void> {
    this.getAssociationObjects(associations, objectType).then((formFields) => {
      this.setAssociationsInObjectAssociations(objectType, formFields, true);
    });
  }

  private async getAssociationObjects(
    associations: FormFieldAssociation[],
    objectType: ObjectType,
  ): Promise<FormFieldAssociation[]> {
    const objectIds = associations.map((association) => association.crmObjectId ?? '');
    const objects = await firstValueFrom(this.crmObjectService.getMultiObject(objectType, objectIds));
    return (objects?.crmObjects ?? []).map((crmObject) => {
      return {
        name: this.displayService.getObjectDisplayName(objectType, crmObject),
        crmObjectType: objectType,
        crmObjectId: crmObject.crmObjectId,
        crmObjectSubtype: crmObject.crmObjectSubtype,
      } as FormFieldAssociation;
    });
  }

  private setAssociationsInObjectAssociations(
    objectType: ObjectType,
    newAssociations: FormFieldAssociation[],
    setAssociations: boolean,
  ): void {
    this.objectAssociations.update((fields) => {
      return fields.map((field) => {
        if (field.type === objectType) {
          return {
            ...field,
            associations: setAssociations ? newAssociations : [...field.associations, ...newAssociations],
          };
        }
        return field;
      });
    });
  }

  updateValueAndValidity(): void {
    this.getSelectedComponent()?.validate();
  }

  expand(): void {
    this.collapsed.set(false);
  }

  collapse(): void {
    this.activityTypeSelected = undefined;
    this.collapsed.set(true);
    this.clearFields();
    this.activityTypeSelected = undefined;
    this.communicationSubTypeSelected = undefined;
  }

  followUpExpandedChanged(isExpanded: boolean): void {
    this.followUpExpanded$$.next(isExpanded);
  }

  handleCancelClicked(): void {
    this.collapse();
    this.cancel.emit();
  }

  handleDefaultStateFocus(): void {
    this.onActivityTypeChanged(DEFAULT_SELECTED_ACTIVITY_TYPE);
  }

  clearFields(): void {
    this.loggerNoteComponent?.resetForm();
    this.loggerEmailComponent?.resetForm();
    this.loggerCallComponent?.resetForm();
    this.loggerMeetingComponent?.resetForm();
    this.loggerTaskComponent?.resetForm();
    this.loggerCommunicationComponent?.resetForm();
    this.setDefaultObjectAssociations();
    this.teamAssociations.set([]);
    this.tags$$.next([]);
    this.sharedDescription = '';
    // Clear uploaded files and optimistic files when form is cleared
    this.fileAssociationService.resetState();
    this.showFileUploader = false;
  }

  private mergeAssociations(): ActivityAssociationInterface[] {
    const extraAssociations = this.extraAssociations || [];
    const mergedAssociations = [] as ActivityAssociationInterface[];
    this.objectAssociations().forEach((association) => {
      mergedAssociations.push(...association.associations);
    });

    // Add file associations based on mode (create vs edit)
    const fileAssociations = this.createFileAssociationsForSave();

    return [...mergedAssociations, ...this.teamAssociations(), ...extraAssociations, ...fileAssociations];
  }

  /**
   * Create file associations for the activity being saved
   * In edit mode: includes both existing files and newly uploaded files
   * In create mode: includes only newly uploaded files
   */
  private createFileAssociationsForSave(): ActivityAssociationInterface[] {
    const isEditing = this.crmActivityId$$.value !== '';

    if (isEditing) {
      return this.createFileAssociationsFromAllFiles();
    } else {
      return this.fileAssociationService.createFileAssociationsFromUploadedFiles();
    }
  }

  /**
   * Convert all files (existing + uploaded) to activity associations for edit mode
   */
  private createFileAssociationsFromAllFiles(): ActivityAssociationInterface[] {
    const allFiles = this.fileAssociationService.getAllFiles();

    return allFiles.map((file) => ({
      crmObjectId: file.fileUrl,
      crmObjectType: 'File' as any,
      crmObjectSubtype: undefined,
    }));
  }

  /**
   * Handle file associations after activity save
   * Creates parent object associations for newly uploaded files if needed
   */
  private handlePostSaveFileAssociations(
    activityId: string,
    objectType: ObjectType,
    objectId: string,
  ): Observable<ActivityInterface> {
    const hasUploadedFiles = this.fileAssociationService.getUploadedFiles().length > 0;

    if (hasUploadedFiles) {
      // Create parent object file associations for newly uploaded files
      return this.createFileAssociationsWithParentObject(objectType, objectId).pipe(
        switchMap(() => this.activityLoggerService.getActivity(activityId)),
      );
    } else {
      // No new files to associate, just fetch the activity
      return this.activityLoggerService.getActivity(activityId);
    }
  }

  /**
   * Create file associations with the parent object (contact/company/etc.) after activity creation
   */
  private createFileAssociationsWithParentObject(objectType: ObjectType, objectId: string): Observable<any> {
    const uploadedFiles = this.fileAssociationService.getUploadedFiles();

    if (uploadedFiles.length === 0) {
      return of(null); // No files to associate
    }

    // Create a file association manager for the parent object
    const parentObjectManager = this.fileAssociationService.createAssociationManager(objectType, objectId, {
      immediateAssociation: true,
    });

    // Create associations for each uploaded file with the parent object
    uploadedFiles.forEach((fileInfo) => {
      parentObjectManager.onFileUploaded(fileInfo);
    });

    // Return a completed observable (the associations are created optimistically)
    return of(true);
  }

  private getPrimaryAccountGroupId(crmObject: CrmObjectInterface): string | null {
    const fieldValue = this.fieldsService.getFieldValueFromCrmObject(
      crmObject,
      PlatformExtensionFieldIds.AccountGroupID,
    );
    return fieldValue?.stringValue ?? null;
  }

  /**
   * Create a mobile layout observable.
   * If layout is set to `mobile`, it will always be `true`.
   * Otherwise, it will match the screen size  (<= `768px`).
   * @returns `Observable<boolean>`: `true` if the layout is mobile, `false` otherwise.
   */
  private setupMobileMatcher(): Observable<boolean> {
    const mobileMedia$ = this.breakpointObserver.observe(`(max-width: ${DEFAULT_MOBILE_BREAKPOINT})`).pipe(
      distinctUntilChanged(),
      map((result) => result.matches),
    );
    return this.layout$$.pipe(
      map((layout) => layout === 'mobile'),
      switchWhenFalsy(mobileMedia$),
    );
  }

  updateTags(tagEvent: string[]): void {
    this.tags$$?.next(tagEvent);
  }

  // File Association Methods (delegated to service)

  get selectedAccessType() {
    return this.fileAssociationService.selectedAccessType;
  }

  set selectedAccessType(value: FileAccessType) {
    this.fileAssociationService.selectedAccessType = value;
  }

  protected readonly fileAccessTypeOptions: UploaderAccessTypeOption[] = [
    { name: 'ASSOCIATIONS.FILES.PRIVATE', value: FileAccessType.PRIVATE },
    { name: 'ASSOCIATIONS.FILES.PUBLIC', value: FileAccessType.PUBLIC },
  ];

  signedUrlUploadHandler = this.fileAssociationManager.signedUrlUploadHandler;

  onFileUploaded(fileInfo: FileInfo): void {
    this.fileAssociationManager.onFileUploaded(fileInfo);
  }

  onFileUploadError = this.fileAssociationManager.onFileUploadError;

  onFilesChanged(files: FileInfo[]): void {
    if (isFileUploadBatchSettled(files)) {
      this.showFileUploader = false;
    }
  }

  /**
   * Clear uploaded files after successful activity creation
   */
  private clearUploadedFiles(): void {
    this.fileAssociationService.clearUploadedFiles();
  }

  // File operations delegated to service
  removeFileAssociation = this.fileAssociationManager.removeFileAssociation;
  downloadFile = this.fileAssociationManager.downloadFile;
  viewFile = this.fileAssociationManager.viewFile;
  getFileSizeDisplay = this.fileAssociationManager.getFileSizeDisplay;
  getFileTypeIcon = this.fileAssociationManager.getFileTypeIcon;
  trackByFileId = this.fileAssociationManager.trackByFileId;

  /**
   * Toggle file uploader modal
   */
  toggleFileUploader(): void {
    this.showFileUploader = !this.showFileUploader;
  }
}
